/**
 * Payloads e normalizações do fluxo request/approve/reject reopen.
 */

/**
 * Valida e normaliza o ID da venda para rotas `/sale/:idsale/...`.
 * @param {string|number} idsale
 * @returns {number|null}
 */
export function normalizeSaleId(idsale) {
  const id = Number(idsale);
  if (!Number.isFinite(id) || id < 1) return null;
  return Math.trunc(id);
}

/**
 * Resolve `notifyUserId` a partir do campo canônico ou aliases do Swagger.
 * @param {Object} [input]
 * @returns {number|null}
 */
export function resolveNotifyUserId(input) {
  const raw =
    input?.notifyUserId ??
    input?.requesterUserId ??
    input?.requestedByUserId;
  const id = Number(raw);
  if (!Number.isFinite(id) || id < 1) return null;
  return Math.trunc(id);
}

/**
 * Resolve motivo opcional (reason / aliases), truncado em maxLength.
 * @param {Object} [input]
 * @param {string[]} [aliasKeys]
 * @param {number} [maxLength]
 * @returns {string|undefined}
 */
export function resolveOptionalReason(
  input,
  aliasKeys = ["reason", "observation", "requesterReason", "rejectReason"],
  maxLength = 5000,
) {
  if (!input || typeof input !== "object") return undefined;

  for (const key of aliasKeys) {
    const raw = input[key];
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    return trimmed.slice(0, maxLength);
  }

  return undefined;
}

/**
 * Body de `POST /sale/{idsale}/request-reopen`.
 * @param {Object} input
 * @param {string} input.recipientToken
 * @param {string} [input.reason]
 * @param {string} [input.observation]
 * @param {string} [input.requesterReason]
 * @param {number} [maxLength]
 * @returns {{ recipientToken: string, reason?: string } | null}
 */
export function buildRequestReopenPayload(input, maxLength = 5000) {
  const recipientToken =
    typeof input?.recipientToken === "string"
      ? input.recipientToken.trim()
      : "";

  if (!recipientToken) return null;

  /** @type {{ recipientToken: string, reason?: string }} */
  const body = { recipientToken };

  const reason = resolveOptionalReason(
    input,
    ["reason", "observation", "requesterReason"],
    maxLength,
  );
  if (reason) body.reason = reason;

  return body;
}

/**
 * Body de `POST /sale/{idsale}/approve-reopen`.
 * @param {Object} input
 * @returns {{ notifyUserId: number } | null}
 */
export function buildApproveReopenPayload(input) {
  const notifyUserId = resolveNotifyUserId(input);
  if (notifyUserId == null) return null;
  return { notifyUserId };
}

/**
 * Body de `POST /sale/{idsale}/reject-reopen`.
 * @param {Object} input
 * @param {number} [maxLength]
 * @returns {{ notifyUserId: number, reason?: string } | null}
 */
export function buildRejectReopenPayload(input, maxLength = 5000) {
  const notifyUserId = resolveNotifyUserId(input);
  if (notifyUserId == null) return null;

  /** @type {{ notifyUserId: number, reason?: string }} */
  const body = { notifyUserId };

  const reason = resolveOptionalReason(
    input,
    ["reason", "rejectReason", "observation"],
    maxLength,
  );
  if (reason) body.reason = reason;

  return body;
}
